// PATH: src/pages/ProvidersPage.tsx
import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddRounded";
import { useAppConfigsApi, useLlmGatewayApi } from "../api";
import type { GatewayProvidersResponse, LlmCatalog, LlmProvider } from "../models/domain";
import { PageHeader, StatusPill, errorMessage, useConfirm, useToast } from "../components/ui";
import DeclareModelDialog, { type DeclareTarget } from "../components/settings/DeclareModelDialog";
import { KeyDialog, ProviderDialog, type ProviderEdit } from "../components/providers/ProviderDialogs";
import { keyLabel, testVerdict } from "../components/providers/providerText";
import AccessTokensCard from "../components/providers/AccessTokensCard";
import { CAP_LABELS, kindLabel, priceLabel, routingLabel } from "../components/settings/llmCatalog";

const CAPS = Object.keys(CAP_LABELS) as (keyof typeof CAP_LABELS)[];

/**
 * « Fournisseurs » : les fournisseurs d'IA (type, URL, actif, données
 * d'élèves) et les modèles déclarés, dans l'app-config Tilmid+ ; l'état des
 * clés, lu sur la passerelle tc_llm_gateway, seule à les détenir. Les rôles
 * choisissent ensuite fournisseur et modèle dans « Réglages Tilmid+ ».
 */
const ProvidersPage: React.FC = () => {
  const { getLlmCatalog, putLlmProvider } = useAppConfigsApi();
  const { listGatewayProviders, testProviderKey, deleteProviderKey } = useLlmGatewayApi();
  const toast = useToast();
  const confirm = useConfirm();

  const [catalog, setCatalog] = React.useState<LlmCatalog | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [gateway, setGateway] = React.useState<GatewayProvidersResponse | null>(null);
  const [gatewayError, setGatewayError] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<ProviderEdit | null>(null);
  const [keyFor, setKeyFor] = React.useState<{ id: string; label: string } | null>(null);
  const [declaring, setDeclaring] = React.useState<DeclareTarget | null>(null);
  const [testing, setTesting] = React.useState<string | null>(null);

  const loadCatalog = React.useCallback(async () => {
    setError(null);
    try {
      setCatalog((await getLlmCatalog()).data);
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger les fournisseurs."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La passerelle est lue à part : injoignable, l'écran reste utilisable pour
  // tout ce qui n'est pas une clé.
  const loadGateway = React.useCallback(async () => {
    setGatewayError(null);
    try {
      setGateway((await listGatewayProviders()).data);
    } catch (e) {
      setGateway(null);
      setGatewayError(errorMessage(e, "Passerelle injoignable."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    void loadCatalog();
    void loadGateway();
  }, [loadCatalog, loadGateway]);

  const keys = React.useMemo(() => new Map((gateway?.providers ?? []).map((p) => [p.id, p])), [gateway]);
  const providers = React.useMemo(
    () => Object.entries(catalog?.providers ?? {}).sort(([, a], [, b]) => a.label.localeCompare(b.label, "fr")),
    [catalog],
  );
  const models = React.useMemo(
    () => Object.entries(catalog?.models ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    [catalog],
  );

  const toggle = async (id: string, p: LlmProvider, enabled: boolean) => {
    if (!enabled) {
      const ok = await confirm({
        title: `Désactiver ${p.label} ?`,
        message:
          "Il ne sera plus proposé dans les Réglages Tilmid+. Refusé tant qu'un rôle ou une matière l'utilise : changez-les d'abord.",
        confirmLabel: "Désactiver",
        danger: true,
      });
      if (!ok) return;
    }
    try {
      setCatalog((await putLlmProvider(id, { ...p, enabled })).data);
      toast.success(enabled ? `${p.label} activé.` : `${p.label} désactivé.`);
    } catch (e) {
      toast.error(errorMessage(e, "Changement refusé."));
    }
  };

  const testStored = async (id: string) => {
    setTesting(id);
    try {
      const { data } = await testProviderKey(id);
      const v = testVerdict(data.test);
      if (v.ok) toast.success(v.text);
      else toast.error(v.text);
    } catch (e) {
      toast.error(errorMessage(e, "Test impossible."));
    } finally {
      setTesting(null);
    }
  };

  const removeKey = async (id: string, label: string) => {
    const ok = await confirm({
      title: `Retirer la clé de ${label} ?`,
      message:
        "La passerelle n'appellera plus ce fournisseur tant qu'une nouvelle clé n'est pas posée : les rôles qui l'utilisent échoueront. Le journal garde l'empreinte de la clé retirée.",
      confirmLabel: "Retirer",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteProviderKey(id);
      toast.success("Clé retirée.");
      void loadGateway();
    } catch (e) {
      toast.error(errorMessage(e, "Retrait refusé."));
    }
  };

  return (
    <Box sx={{ maxWidth: 1280 }}>
      <PageHeader
        title="Fournisseurs"
        subtitle="Les fournisseurs d'IA et leurs clés, puis les modèles déclarés avec leurs capacités et leurs tarifs. Les clés sont gardées chiffrées par la passerelle : on n'en voit que les 4 derniers caractères. Chaque agent choisit ensuite son fournisseur et son modèle dans « Réglages Tilmid+ »."
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void loadCatalog()}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {gatewayError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void loadGateway()}>
              Réessayer
            </Button>
          }
        >
          Passerelle injoignable ({gatewayError}) : l'état des clés est inconnu, on ne peut ni les tester ni les changer.
          Fournisseurs et modèles restent modifiables.
        </Alert>
      )}
      {gateway?.config_problem && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          La passerelle ne lit pas la configuration : {gateway.config_problem}
        </Alert>
      )}
      {catalog?.prices_error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Tarifs illisibles ({catalog.prices_error}) : aucun modèle ne peut être déclaré pour l'instant.
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2.5 }}>
          <Box>
            <Typography variant="h5">Fournisseurs</Typography>
            <Typography variant="body2" color="text.secondary">
              Un fournisseur désactivé n'est plus proposé aux agents. Changer son URL rend sa clé inutilisable.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!catalog}
            onClick={() =>
              setEditing({
                id: "",
                provider: { label: "", kind: "openai_compatible", enabled: true, student_data_allowed: false },
              })
            }
          >
            Ajouter un fournisseur
          </Button>
        </Stack>
        {!catalog ? (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Skeleton height={48} />
            <Skeleton height={48} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fournisseur</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>URL de base</TableCell>
                  <TableCell>Clé</TableCell>
                  <TableCell>Données d'élèves</TableCell>
                  <TableCell>Actif</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {providers.map(([id, p]) => {
                  const gw = keys.get(id);
                  const label = keyLabel(gw?.key);
                  const problem = gw?.problem || gw?.key.problem;
                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 650 }}>
                          {p.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {id}
                        </Typography>
                      </TableCell>
                      <TableCell>{kindLabel(p.kind)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                          {p.base_url || (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                              URL publique
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {!gateway ? (
                          <Typography variant="caption" color="text.secondary">
                            inconnu
                          </Typography>
                        ) : label ? (
                          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {label}
                          </Typography>
                        ) : (
                          <StatusPill tone="warning">aucune clé</StatusPill>
                        )}
                        {problem && (
                          <Typography variant="caption" color="error" component="div">
                            {problem}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusPill tone={p.student_data_allowed ? "success" : "neutral"}>
                          {p.student_data_allowed ? "autorisé" : "non"}
                        </StatusPill>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={p.enabled}
                          onChange={(e) => void toggle(id, p, e.target.checked)}
                          inputProps={{ "aria-label": `${p.label} actif` }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Button
                          size="small"
                          disabled={!gw?.key.key_set || testing === id}
                          onClick={() => void testStored(id)}
                        >
                          {testing === id ? "Test…" : "Tester"}
                        </Button>
                        <Button size="small" disabled={!gateway} onClick={() => setKeyFor({ id, label: p.label })}>
                          Changer la clé
                        </Button>
                        <Button size="small" onClick={() => setEditing({ id, provider: p })}>
                          Modifier
                        </Button>
                        {gw?.key.key_set && (
                          <Button size="small" color="error" onClick={() => void removeKey(id, p.label)}>
                            Retirer la clé
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Card>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2.5 }}>
          <Box>
            <Typography variant="h5">Modèles</Typography>
            <Typography variant="body2" color="text.secondary">
              Un modèle sert un agent seulement s'il est déclaré, tarifé, et fait ce que l'agent demande.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!catalog || providers.length === 0}
            onClick={() => setDeclaring({ provider: providers[0]?.[0] ?? "", model: "" })}
          >
            Déclarer un modèle
          </Button>
        </Stack>
        {catalog && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Modèle</TableCell>
                  <TableCell>Capacités</TableCell>
                  <TableCell align="right">Sortie max.</TableCell>
                  <TableCell align="right">
                    Tarif ($ / M jetons) : entrée · cache · sortie ; OCR dédié : $ / 1 000 pages ; voix : jetons, minute ou caractères
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {models.map(([key, m]) => (
                  <TableRow key={key}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 650 }}>
                        {m.model}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {catalog.providers[m.provider]?.label ?? m.provider}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {CAPS.filter((c) => m[c]).map((c) => (
                          <Chip key={c} size="small" label={CAP_LABELS[c]} />
                        ))}
                        {m.reasoning && m.default_effort && <Chip size="small" variant="outlined" label={`effort ${m.default_effort}`} />}
                        {m.max_tokens_only && <Chip size="small" variant="outlined" label="max_tokens seul" />}
                        {m.response_healing && <Chip size="small" variant="outlined" label="JSON réparé" />}
                        {m.routing && <Chip size="small" color="primary" variant="outlined" label={routingLabel(m.routing)} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {m.max_output.toLocaleString("fr-FR")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                      {priceLabel(m) ?? <StatusPill tone="error">sans tarif</StatusPill>}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setDeclaring({ provider: m.provider, model: m.model })}>
                        Modifier
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <AccessTokensCard />

      <ProviderDialog
        edit={editing}
        existing={Object.keys(catalog?.providers ?? {})}
        onClose={() => setEditing(null)}
        onSaved={(c) => {
          setCatalog(c);
          toast.success("Fournisseur enregistré.");
          void loadGateway();
        }}
      />
      <KeyDialog
        provider={keyFor}
        onClose={() => setKeyFor(null)}
        onSaved={() => {
          toast.success("Clé enregistrée.");
          void loadGateway();
        }}
      />
      <DeclareModelDialog
        target={declaring}
        providers={catalog?.providers ?? {}}
        models={catalog?.models}
        onClose={() => setDeclaring(null)}
        onDeclared={(c) => {
          setCatalog(c);
          toast.success("Modèle déclaré.");
        }}
      />
    </Box>
  );
};

export default ProvidersPage;
